<template>
  <div class="top-performance-dashboard" v-if="showTopPerformance">
    <div class="dashboard-title">TOP PERFORMERS</div>
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!hasAnyData" class="empty-state">
      <div class="empty-icon">📊</div>
      <p>No data available</p>
      <button class="refresh-btn" @click="fetchPerformanceData">Refresh</button>
    </div>

    <!-- Dashboard Content - All 4 boxes in one row -->
    <div v-else class="dashboard">
      <!-- 4 Column Grid -->
      <div class="four-column-grid">
        
        <!-- Card 1: Today: Top Salesperson -->
        <div class="compact-card">
          <div class="card-header">
            <span class="badge today-badge">TODAY</span>
            <span class="crown">👑</span>
          </div>

          <div style="display: flex;">
            <div class="stat">
              <span class="stat-label">Gross Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(dayTopSalesperson.grossSales) }}</strong>
            </div>
            <div class="user">  
              <div class="avatar-sm" @click="openUserImage(dayTopSalesperson.name)">
                <img v-if="getUserImageUrl(dayTopSalesperson.name)" 
                     :src="getUserImageUrl(dayTopSalesperson.name)" 
                     @error="onImageError"/>
                <div v-else class="avatar-initials">{{ getInitials(dayTopSalesperson.name) }}</div>
              </div>
              <div class="card-name">{{ dayTopSalesperson.name || '—' }}</div>
              <div class="card-subtitle">Top Seller</div>
            </div>
            <div class="stat">
              <span class="stat-label">Net Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(dayTopSalesperson.netSales) }}</strong>
            </div>
          </div>

          <div class="card-stats">
            <div class="stat">
              <span class="stat-label">Transactions</span></br>
              <strong style="font-size: 17px;">{{ dayTopSalesperson.numberOfSales }}</strong>
            </div>
          </div>
        </div>

        <!-- Card 2: Today: Top Location -->
        <div class="compact-card">
          <div class="card-header">
            <span class="badge today-badge">TODAY</span>
            <span class="location-icon">📍</span>
          </div>

          <div style="display: flex;">
            <div class="stat">
              <span class="stat-label">Gross Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(dayTopLocation.grossSales) }}</strong>
            </div>
            <div class="user">
              <div class="avatar-sm store-avatar" @click="openLocationDetails(dayTopLocation.name)">
                <div class="store-icon-initials">🏬</div>
              </div>
              <div class="card-name">{{ dayTopLocation.name || '—' }}</div>
              <div class="card-subtitle">Top Store</div>
            </div>
            <div class="stat">
              <span class="stat-label">Net Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(dayTopLocation.netSales) }}</strong>
            </div>
          </div>

          <div class="card-stats">
            <div class="stat">
              <span class="stat-label">Transactions</span></br>
              <strong style="font-size: 17px;">{{ dayTopLocation.numberOfSales }}</strong>
            </div>
          </div>
        </div>

        <!-- Card 3: This Week: Top Salesperson -->
        <div class="compact-card">
          <div class="card-header">
            <span class="badge week-badge">THIS WEEK</span>
            <span class="crown">👑</span>
          </div>

          <div style="display: flex;">
            <div class="stat">
              <span class="stat-label">Gross Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(weekTopSalesperson.grossSales) }}</strong>
            </div>
            <div class="user">
              <div class="avatar-sm" @click="openUserImage(weekTopSalesperson.name)">
                <img v-if="getUserImageUrl(weekTopSalesperson.name)" 
                     :src="getUserImageUrl(weekTopSalesperson.name)" 
                     @error="onImageError"/>
                <div v-else class="avatar-initials">{{ getInitials(weekTopSalesperson.name) }}</div>
              </div>
              <div class="card-name">{{ weekTopSalesperson.name || '—' }}</div>
              <div class="card-subtitle">Top Seller</div>
            </div>
            <div class="stat">
              <span class="stat-label">Net Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(weekTopSalesperson.netSales) }}</strong>
            </div>
          </div>

          <div class="card-stats">
            <div class="stat">
              <span class="stat-label">Transactions</span></br>
              <strong style="font-size: 17px;">{{ weekTopSalesperson.numberOfSales }}</strong>
            </div>
          </div>
        </div>

        <!-- Card 4: This Week: Top Location -->
        <div class="compact-card">
          <div class="card-header">
            <span class="badge week-badge">THIS WEEK</span>
            <span class="location-icon">📍</span>
          </div>

          <div style="display: flex;">
            <div class="stat">
              <span class="stat-label">Gross Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(weekTopLocation.grossSales) }}</strong>
            </div>
            <div class="user">
              <div class="avatar-sm store-avatar" @click="openLocationDetails(weekTopLocation.name)">
                <div class="store-icon-initials">🏬</div>
              </div>
              <div class="card-name">{{ weekTopLocation.name || '—' }}</div>
              <div class="card-subtitle">Top Store</div>
            </div>
            <div class="stat">
              <span class="stat-label">Net Sales</span></br>
              <strong style="font-size: 17px;">${{ formatNumber(weekTopLocation.netSales) }}</strong>
            </div>
          </div>

          <div class="card-stats">
            <div class="stat">
              <span class="stat-label">Transactions</span></br> 
              <strong style="font-size: 17px;">{{ weekTopLocation.numberOfSales }}</strong>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Image Modal -->
    <div class="image-modal" v-if="showImageModal" @click="closeImageModal">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="closeImageModal">✕</button>
        <img :src="modalImageUrl" :alt="modalImageName" />
        <h4>{{ modalImageName }}</h4>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Requestor from '@/utilities/Requestor';

export default {
  name: "TopPerformance2",
  props: {
    userDetails: { type: Object, default: () => ({}) },
    showTopPerformance: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      usersList: [],
      locationsList: [], // Dynamic locations from API
      showImageModal: false,
      modalImageUrl: '',
      modalImageName: '',
      
      dayTopSalesperson: { name: '—', netSales: 0, grossSales: 0, numberOfSales: 0 },
      dayTopLocation: { name: '—', netSales: 0, grossSales: 0, numberOfSales: 0 },
      
      weekTopSalesperson: { name: '—', netSales: 0, grossSales: 0, numberOfSales: 0 },
      weekTopLocation: { name: '—', netSales: 0, grossSales: 0, numberOfSales: 0 }
    };
  },
  computed: {
    hasAnyData() {
      // Check if any of the top performers have actual data
      const hasTodaySalesperson = this.dayTopSalesperson.name !== '—' && this.dayTopSalesperson.grossSales > 0;
      const hasTodayLocation = this.dayTopLocation.name !== '—' && this.dayTopLocation.grossSales > 0;
      const hasWeekSalesperson = this.weekTopSalesperson.name !== '—' && this.weekTopSalesperson.grossSales > 0;
      const hasWeekLocation = this.weekTopLocation.name !== '—' && this.weekTopLocation.grossSales > 0;
      
      return hasTodaySalesperson || hasTodayLocation || hasWeekSalesperson || hasWeekLocation;
    }
  },
  methods: {
    async fetchPerformanceData() {
      this.loading = true;
      try {
        // Fetch users and locations in parallel
        await Promise.all([
          this.fetchUsers(),
          this.fetchLocations()
        ]);
        
        const response = await this.postRequest("/api/reports/reports_top_performers/", {
          username: this.userDetails.username,
          location_id: null,
          salesperson: null
        });
        
        // Extract the result data (handle different response structures)
        let result = null;
        if (response && response.data && response.data.result) {
          result = response.data.result;
        } else if (response && response.result) {
          result = response.result;
        } else if (response && response.data) {
          result = response.data;
        }
        
        if (result) {
          // Map location IDs to names using dynamic locations list
          if (result.today && result.today.top_location && result.today.top_location.name) {
            const locationId = result.today.top_location.name;
            result.today.top_location.name = this.getLocationName(locationId);
          }
          
          if (result.this_week && result.this_week.top_location && result.this_week.top_location.name) {
            const locationId = result.this_week.top_location.name;
            result.this_week.top_location.name = this.getLocationName(locationId);
          }
          
          // Today's top salesperson
          if (result.today && result.today.top_salesperson) {
            this.dayTopSalesperson = {
              name: result.today.top_salesperson.name || '—',
              netSales: result.today.top_salesperson.net_sales || 0,
              grossSales: result.today.top_salesperson.gross_sales || 0,
              numberOfSales: result.today.top_salesperson.transactions || 0
            };
          }
          
          // Today's top location
          if (result.today && result.today.top_location) {
            this.dayTopLocation = {
              name: result.today.top_location.name || '—',
              netSales: result.today.top_location.net_sales || 0,
              grossSales: result.today.top_location.gross_sales || 0,
              numberOfSales: result.today.top_location.transactions || 0
            };
          }
          
          // This week's top salesperson
          if (result.this_week && result.this_week.top_salesperson) {
            this.weekTopSalesperson = {
              name: result.this_week.top_salesperson.name || '—',
              netSales: result.this_week.top_salesperson.net_sales || 0,
              grossSales: result.this_week.top_salesperson.gross_sales || 0,
              numberOfSales: result.this_week.top_salesperson.transactions || 0
            };
          }
          
          // This week's top location
          if (result.this_week && result.this_week.top_location) {
            this.weekTopLocation = {
              name: result.this_week.top_location.name || '—',
              netSales: result.this_week.top_location.net_sales || 0,
              grossSales: result.this_week.top_location.gross_sales || 0,
              numberOfSales: result.this_week.top_location.transactions || 0
            };
          }
        }
      } catch (e) {
        console.error('Error fetching performance data:', e);
      } finally {
        this.loading = false;
      }
    },
    
    async fetchUsers() {
      try {
        const response = await new Promise((resolve, reject) => {
          Requestor.getSyncP("/api/user/list_users/", {}, this, resolve, reject);
        });
        if (response.data.result) this.usersList = response.data.result;
      } catch (e) {
        console.error('Failed to fetch users:', e);
      }
    },
    
    async fetchLocations() {
      try {
        const response = await new Promise((resolve, reject) => {
          Requestor.getSyncP("/api/location/get_all_locations/", {}, this, resolve, reject);
        });
        if (response.data && response.data.result) {
          this.locationsList = response.data.result;
        } else if (response && response.result) {
          this.locationsList = response.result;
        }
      } catch (e) {
        console.error('Failed to fetch locations:', e);
        // Fallback to empty array - location names will show as IDs
        this.locationsList = [];
      }
    },
    
    getLocationName(locationId) {
      if (!locationId || locationId === '—') return '—';
      
      // Try to find location by ID (convert to number for comparison)
      const location = this.locationsList.find(loc => loc.location_id === parseInt(locationId));
      
      if (location && location.location_name) {
        return location.location_name;
      }
      
      // Fallback: return the ID if no name found
      return locationId;
    },
    
    getUserImageUrl(username) {
      if (!username || username === '—' || !this.usersList.length) return null;
      const user = this.usersList.find(u => u.username === username);
      if (!user || !user.properties || !user.properties.image_url) return null;
      let url = user.properties.image_url.trim();
      if (url === '' || url === 'null' || url === 'undefined') return null;
      return url;
    },
    
    getInitials(name) {
      if (!name || name === '—') return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },
    
    onImageError(e) {
      e.target.style.display = 'none';
      if (e.target.parentElement) {
        const initialsDiv = e.target.parentElement.querySelector('.avatar-initials');
        if (initialsDiv) initialsDiv.classList.add('show');
      }
    },
    
    openUserImage(name) {
      if (!name || name === '—') return;
      const url = this.getUserImageUrl(name);
      if (url) {
        this.modalImageUrl = url;
        this.modalImageName = name;
        this.showImageModal = true;
      }
    },
    
    openLocationDetails(locationName) {
      console.log('Location clicked:', locationName);
      // You can add additional functionality here, like opening a modal with location details
    },
    
    closeImageModal() {
      this.showImageModal = false;
    },
    
    postRequest(path, data) {
      return new Promise((resolve, reject) => {
        Requestor.postSyncP(path, data, this, resolve, reject);
      });
    },
    
    formatNumber(n) {
      if (isNaN(n) || n === undefined || n === null) return '0';
      return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
  },
  mounted() {
    this.fetchPerformanceData();
  }
};
</script>

<style scoped>

.dashboard-title{
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-left: 14px; 
}
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.top-performance-dashboard {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  padding: 16px 20px;
  margin-top: 12px;
  backdrop-filter: blur(10px);
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  gap: 12px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e2e8f0;
  border-top-color: #f59e0b;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-container p {
  color: #64748b;
  font-size: 13px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 30px;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
  opacity: 0.6;
}

.empty-state p {
  color: #64748b;
  font-size: 13px;
  margin-bottom: 12px;
}

.refresh-btn {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

/* Dashboard */
.dashboard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding:10px;
}

/* 4 Column Grid */
.four-column-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.compact-card {
  background: #f8fafc;
  border-radius: 14px;
  padding: 14px 10px;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.compact-card:hover {
  transform: translateY(-2px);
  background: #f1f5f9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 4px;
}

.badge {
  font-size: 9px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  letter-spacing: 0.3px;
}

.today-badge {
  background: #fef3c7;
  color: #d97706;
}

.week-badge {
  background: #dbeafe;
  color: #2563eb;
}

.crown, .location-icon {
  font-size: 14px;
}

/* User section styling */
.user {
  flex: 1;
  text-align: center;
}

.avatar-sm {
  width: 52px;
  height: 52px;
  margin: 0 auto 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  padding: 2px;
  cursor: pointer;
  transition: transform 0.2s;
}

.avatar-sm:hover {
  transform: scale(1.05);
}

.avatar-sm img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: white;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: #f59e0b;
}

.store-avatar {
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  cursor: pointer;
}

.store-icon-initials {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.card-name {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
  text-transform: uppercase;
}

.card-subtitle {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 0;
}

.card-stats {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.stat {
  flex: 1;
  text-align: center;
}

.stat:first-child, .stat:last-child {
  flex: 0.8;
}

.stat-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  margin-bottom: 4px;
  letter-spacing: 0.3px;
}

.stat strong {
  font-size: 13px;
  font-weight: 800;
  color: #1e293b;
}

/* Modal */
.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 20px;
  text-align: center;
  position: relative;
  max-width: 280px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.modal-content img {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 12px;
}

.modal-content h4 {
  font-size: 14px;
  color: #1e293b;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #64748b;
}

/* Responsive */
@media (max-width: 900px) {
  .four-column-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

@media (max-width: 500px) {
  .top-performance-dashboard {
    padding: 12px;
  }
  
  .four-column-grid {
    grid-template-columns: 1fr;
  }
  
  .stat strong {
    font-size: 12px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Homepagecomponents/TopPerformance2.vue